import {
  Analytics,
  Assignment,
  CompareArrows,
  Dashboard,
  Description,
  Factory,
  FactCheck,
  Science,
  Settings,
  ShowChart,
  Storage,
  Store,
  Warehouse,
} from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../hooks/redux'
import { useGetMeQuery } from '../store/api/authApi'
import { PageDataState } from '../components/PageDataState'

interface QuickLink {
  title: string
  subtitle: string
  path: string
  icon: typeof Analytics
  color: string
  /** Function key trong /settings/roles. Tile chỉ hiện khi permissions[permKey] !== 'NoGrant'. */
  permKey: string
}

// 1:1 với danh sách function tại /settings/roles (SettingsRoleDialog PERMISSION_ROWS).
// Mỗi function permission = 1 Quick Access tile; account nào có quyền (Edit/View) function đó mới hiện tile.
const quickLinks: QuickLink[] = [
  {
    title: 'Report',
    subtitle: 'PnL, Risk, Long Short, Cashflows',
    path: '/dashboard/pnl',
    icon: Analytics,
    color: '#4a3d8f',
    permKey: 'position',
  },
  {
    title: 'Sales Contract',
    subtitle: 'Contracts, consignment, amendment',
    path: '/sales/contract/list',
    icon: Assignment,
    color: '#145a59',
    permKey: 'saleContract',
  },
  {
    title: 'Sales Control',
    subtitle: 'Local sales and delivery workflow',
    path: '/sales/control/local-sales',
    icon: Store,
    color: '#0b3633',
    permKey: 'saleControl',
  },
  {
    title: 'Purchase',
    subtitle: 'Purchases, NPE, PTBF, amendments',
    path: '/purchase/contract/list',
    icon: Description,
    color: '#a14b2a',
    permKey: 'purchase',
  },
  {
    title: 'Procurement',
    subtitle: 'Purchase, pay data, invoices',
    path: '/procurement/purchase',
    icon: FactCheck,
    color: '#da833c',
    permKey: 'procurement',
  },
  {
    title: 'Futures',
    subtitle: 'Orders, allocation, position, PnL',
    path: '/futures/summary',
    icon: ShowChart,
    color: '#bc2434',
    permKey: 'futures',
  },
  {
    title: 'Factory control',
    subtitle: 'Factory overview and summary',
    path: '/factory/summary',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factory',
  },
  {
    title: 'Bảo Lộc factory',
    subtitle: 'Bao Loc factory dashboard',
    path: '/baoloc',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryBl',
  },
  {
    title: 'Nhơn Trạch factory',
    subtitle: 'Nhon Trach factory dashboard',
    path: '/nhontrach',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryNt',
  },
  {
    title: 'An Phước factory',
    subtitle: 'An Phuoc factory dashboard',
    path: '/nguyencuong',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryNc',
  },
  {
    title: 'Nông Sản factory',
    subtitle: 'Nong San factory dashboard',
    path: '/nongsan',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryNs',
  },
  {
    title: 'Pacorini factory',
    subtitle: 'Pacorini factory dashboard',
    path: '/pacorini',
    icon: Factory,
    color: '#8f5a2e',
    permKey: 'factoryPaco',
  },
  {
    title: 'Matching P-S',
    subtitle: 'Match - Instruct, Allocating, S/P Contract',
    path: '/matching',
    icon: CompareArrows,
    color: '#2e6e6c',
    permKey: 'matchingPs',
  },
  {
    title: 'Quality',
    subtitle: 'Sampling, PSS label, Nestle sample',
    path: '/quality/sampling',
    icon: Science,
    color: '#5b8f3a',
    permKey: 'quality',
  },
  {
    title: 'Virtual Warehouse',
    subtitle: 'Receipts, notes, quality discount',
    path: '/virtual-warehouse/received',
    icon: Warehouse,
    color: '#145a59',
    permKey: 'virtualWarehouse',
  },
  {
    title: 'Master Data',
    subtitle: 'Vendors, items, packages, references',
    path: '/master-data/futures-control/calendar',
    icon: Storage,
    color: '#3d5a80',
    permKey: 'masterData',
  },
  {
    title: 'Setting',
    subtitle: 'Roles, users, log history',
    path: '/settings/roles',
    icon: Settings,
    color: '#5a5a5a',
    permKey: 'setting',
  },
]

const statusCards = [
  { label: 'Today Workflow', value: 'Ready', helper: 'Open module shortcuts from one place', progress: 88 },
  { label: 'ERP Coverage', value: '17 modules', helper: 'Sales, Purchase, Futures, Quality and more', progress: 72 },
  { label: 'Data Access', value: 'Secured', helper: 'Role based access is applied after login', progress: 95 },
]

export default function HomePage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const token = useAppSelector((state) => state.auth.token)
  const userFromAuth = useAppSelector((state) => state.auth.user)
  const permissions = useAppSelector((state) => state.auth.permissions)
  const { data: user, isLoading, error } = useGetMeQuery(undefined, { skip: !token })

  const displayUser = user ?? userFromAuth

  const visibleQuickLinks = useMemo(() => {
    const hasPerms = Object.keys(permissions).length > 0
    if (!hasPerms) return quickLinks
    return quickLinks.filter((link) => permissions[link.permKey] !== 'NoGrant')
  }, [permissions])
  const greetingName = displayUser?.full_name || displayUser?.username || 'You'
  const today = useMemo(
    () =>
      new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  if (isLoading && !displayUser) {
    return <PageDataState isLoading />
  }
  if (error && !displayUser) {
    return <PageDataState error="Failed to load user information" />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Card
        sx={{
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #0b3633 0%, #145a59 58%, #0f2b28 100%)'
              : 'linear-gradient(135deg, #fff9f4 0%, #f7ead3 52%, #e4f0ed 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -72,
            top: -72,
            width: 220,
            height: 220,
            borderRadius: '50%',
            backgroundColor: alpha('#145a59', 0.14),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: -56,
            bottom: -72,
            width: 190,
            height: 190,
            borderRadius: '50%',
            backgroundColor: alpha('#da833c', 0.12),
          }}
        />
        <CardContent sx={{ position: 'relative', p: { xs: 2.5, md: 3.5 } }}>
          <Box>
            <Chip
              size="small"
              icon={<Dashboard />}
              label={today}
              sx={{
                mb: 1.5,
                fontWeight: 800,
                color: 'primary.dark',
                bgcolor: alpha('#f4df9c', 0.52),
                border: `1px solid ${alpha('#da833c', 0.22)}`,
              }}
            />
            <Typography variant="h4" fontWeight={900} sx={{ color: 'primary.dark', letterSpacing: -0.6 }}>
              Hello, {greetingName}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 680, fontWeight: 500 }}>
              ERP Center for Sales, Purchase, Futures, Factory, Warehouse and Settings. Select a module to
              continue your work faster.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {statusCards.map((card) => (
          <Grid item xs={12} md={4} key={card.label}>
                <Card
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.dark, 0.1),
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,249,244,0.84))',
                  }}
                >
              <CardContent>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  {card.label}
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.75 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, minHeight: 40 }}>
                  {card.helper}
                </Typography>
                <LinearProgress variant="determinate" value={card.progress} sx={{ mt: 2, height: 7, borderRadius: 8 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography variant="h6" fontWeight={800}>
            Quick Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Main ERP modules
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          {visibleQuickLinks.map((item) => {
            const Icon = item.icon
            return (
              <Grid item xs={12} sm={6} lg={4} key={item.title}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,249,244,0.86))',
                    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: 6,
                      borderColor: alpha(item.color, 0.5),
                    },
                  }}
                  onClick={() => navigate(item.path)}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          color: item.color,
                          backgroundColor: alpha(item.color, 0.12),
                          border: `1px solid ${alpha(item.color, 0.18)}`,
                          flexShrink: 0,
                        }}
                      >
                        <Icon />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={800}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {item.subtitle}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Box>
    </Box>
  )
}
